import type { ApiResponse } from '../../types/commons';

/**
 * User-facing copy for the financial-statement upload failures the backend
 * raises, keyed by the stable `errorCode` on `ApiResponse`.
 *
 * WHY KEY ON THE CODE, NOT THE MESSAGE: the server's `errors` prose is not a
 * contract. It changes with the backend's `ErrorDisplayOptions:IncludeDiagnosticDetail`
 * setting, which differs per environment — so a toast keyed on message text
 * behaves differently in Development than in Production. Worse, the detailed
 * Development text previews the extracted document text, which is client
 * financial data we must not render in a toast.
 *
 * These codes can arrive from BOTH filing-session endpoints:
 *   - `POST .../files`  — the primary point; the user finds out at upload time.
 *   - `POST .../submit` — the backstop, for files uploaded before the backend
 *     guard shipped. It can additionally raise `FS_FILE_MISSING`.
 * Both funnel through `FilingSessionUploader`, so one mapping serves both; no
 * separate code path is needed per endpoint.
 *
 * Copy was agreed with the backend team when they shipped the guard on
 * 2026-08-14, so both halves of the system describe the same failure the same
 * way. It is rendered as HTML by PrimeNG's MessageService, hence the `<b>`.
 *
 * @see `FilingSessionUploader.emitError` — the only consumer.
 */
export const FS_ERROR_TOASTS: Readonly<Record<string, string>> = Object.freeze({
  /**
   * The file was read fine, but its text never contains the phrase "Financial
   * Statement" — almost always the wrong document was picked. Remedy: choose a
   * different file.
   */
  FS_MARKER_NOT_FOUND:
    "That doesn't look like a Financial Statement. Please upload the financial " +
    "statement document — it should contain the words 'Financial Statement'.",

  /**
   * No text could be extracted at all — usually a scanned or image-only PDF, or
   * a corrupt file. Remedy: re-export THIS document with a real text layer.
   *
   * Deliberately distinct copy from `FS_MARKER_NOT_FOUND`: the remedies differ
   * ("pick another file" vs "re-export this one"), which is exactly why the
   * backend ships two codes. Do not collapse them into one toast.
   */
  FS_TEXT_UNREADABLE:
    "We couldn't read any text from that file. Scanned or image-only PDFs " +
    'aren\'t supported — please upload a text-based PDF or Word document.',

  /**
   * Submit ran and no uploaded file's name ends in `_FS`. In normal flow the
   * user never sees this — `XbrlFormComponent`'s `noFSFile` validator disables
   * the submit button first — so reaching it means the client-side rule and the
   * server's have drifted apart.
   */
  FS_FILE_MISSING:
    'Please upload your financial statement, named with an <b>_FS</b> suffix ' +
    '(for example <b>Initrode_FS.pdf</b>).',

  /**
   * No uploaded document states the company name against a recognisable label
   * (`Name of company`, `Name of the company`, `Company name`).
   *
   * UNLIKE THE OTHER THREE, THIS IS A VERDICT ABOUT THE WHOLE DOCUMENT SET, and
   * it can only arrive from `submit` — never from an upload. The company name
   * frequently lives in the Annual Return rather than the `_FS` file, so the
   * backend checks the combined text of every uploaded file; a per-file check
   * would reject a perfectly good financial statement for a name that arrives
   * with the next upload. **Never render this against a file row** — the
   * offending document is usually not the last one added. (Our single toast
   * channel makes that structurally impossible today; keep it that way if
   * per-file error slots are ever introduced.)
   *
   * The remedy to communicate is "add a document that names the company",
   * typically the Annual Return — not "replace the file you just uploaded".
   *
   * COPY IS FIXED BY THE P.O., recorded in the user story ticket — this exact
   * wording, not the backend's or ours. It reads oddly for a set-wide verdict
   * ("this document") and does not name the Annual Return remedy; that is a
   * known trade-off, not an oversight. Do not reword it here. A spec asserts it
   * character-for-character so an "improvement" fails the build.
   */
  FS_COMPANY_NAME_NOT_FOUND:
    'Company Name not found in this document. Please upload again.',
});

/**
 * Resolves the toast copy for a failed response, or `null` when this is not a
 * financial-statement validation failure.
 *
 * Returning `null` rather than a fallback string is deliberate: it keeps the
 * decision of what to show for everything else with the caller, which already
 * has richer context (the local `File`, the fetched size limits) than this
 * DI-free helper does.
 */
export function resolveFsErrorMessage(response: ApiResponse): string | null {
  const code = response?.errorCode;
  if (typeof code !== 'string') return null;
  return FS_ERROR_TOASTS[code] ?? null;
}
